import React, { useEffect, useCallback } from 'react';
import { Route, Routes } from 'react-router-dom';
import {
	AppInsightsContext,
	AppInsightsErrorBoundary
} from '@microsoft/applicationinsights-react-js';
import { ErrorPage } from './pages/Authentication/ErrorPage';
import { MissingRoute } from './pages/Utility/MissingRoute';
import { useAuthorization } from './hooks/useAuthorization';

// Import Routes
import { authProtectedRoutes, publicRoutes } from './routes/';
import { PrivateRoute } from './routes/PrivateRoute';

// layouts
import VerticalLayout from './components/VerticalLayout/';

// Import scss
import './theme.scss';
import 'design-system/dist/css/main.css';
import 'approval-flow/dist/css/main.css';

import { axiosWrapper } from 'api';
import { useAuth0 } from '@auth0/auth0-react';
import { reactPlugin } from './utils/logger/AppInsights';
import { useErrorTracking } from './utils/logger/ErrorTracking';
import { LoadingScreen } from './components/Common/LoadingScreen';
import { CULTURE_LANG, CULTURE_INFO } from 'globalconfig';

const App = () => {
	const {
		isLoading: isAuthLoading,
		isAuthenticated,
		getAccessTokenSilently,
		error,
		user
	} = useAuth0();
	const { hasAllowedAction } = useAuthorization(user?.['https://app.ofca.io/allowed-actions']);

	useEffect(() => {
		localStorage.setItem(
			'culture',
			user?.['https://app.ofca.io/culture'] || `${CULTURE_LANG}-${CULTURE_INFO}`
		);

		return () => {
			localStorage.removeItem('culture');
		};
	}, [user]);

	useEffect(() => {
		axiosWrapper.setTokenProvider(getAccessTokenSilently);
	}, [getAccessTokenSilently]);

	useErrorTracking();

	const hasPrivateAccess = useCallback(
		(route) =>
			route?.actions === undefined ||
			route?.actions.length === 0 ||
			route.actions.some((a) => hasAllowedAction(a)),
		[hasAllowedAction]
	);

	const hasPublicAccess = (path) =>
		path !== '/signup' ||
		process.env.REACT_APP_ENVIRONMENT === 'TRIAL' ||
		process.env.REACT_APP_ENVIRONMENT === 'DEVELOPMENT';

	if (isAuthLoading) {
		return <LoadingScreen />;
	}

	if (error) {
		reactPlugin.trackException({
			error: 'Auth0Error: ' + error
		});
		return <ErrorPage errorMessage={error.message} />;
	}

	return (
		<AppInsightsErrorBoundary reactPlugin={reactPlugin}>
			<AppInsightsContext.Provider value={reactPlugin}>
				<Routes>
					{publicRoutes.map(
						({ path, component }) =>
							// signup page only available on trial
							hasPublicAccess(path) && (
								<Route
									key={path}
									path={path}
									element={React.createElement(component)}
								/>
							)
					)}
					{authProtectedRoutes.map(
						(route) =>
							hasPrivateAccess(route) && (
								<Route
									key={route.path}
									path={route.path}
									element={
										<PrivateRoute
											isAuthenticated={isAuthenticated}
											state={route.state}
											layout={VerticalLayout}
											component={route.component}
											isMobileRoute={route.isMobileRoute}
										/>
									}
								/>
							)
					)}
					<Route
						path='*'
						element={<MissingRoute />}
					/>
				</Routes>
			</AppInsightsContext.Provider>
		</AppInsightsErrorBoundary>
	);
};

export default App;
